import { NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { ApiService } from './core/api.service';
import { AuthService } from './core/auth.service';
import { MENU_GROUPS, MenuGroup, MenuPermission } from './core/menu-permissions';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterLink, ReactiveFormsModule, NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault],
  template: `
    <router-outlet *ngIf="!auth.isLoggedIn"></router-outlet>

    <div
      class="shell"
      *ngIf="auth.isLoggedIn"
      [class.menu-open]="menuAberto || menuFixo"
      [class.menu-fixed]="menuFixo"
      [class.menu-collapsed]="painelRecolhido"
    >
      <button
        class="menu-toggle"
        type="button"
        (click)="toggleMenu()"
        [attr.aria-expanded]="menuAberto || menuFixo"
        aria-controls="main-sidebar"
        [attr.aria-label]="menuAberto || menuFixo ? 'Recolher menu' : 'Abrir menu'"
      >
        <span aria-hidden="true"></span>
        <span aria-hidden="true"></span>
        <span aria-hidden="true"></span>
      </button>

      <button
        class="menu-backdrop"
        type="button"
        (click)="fecharMenu()"
        aria-label="Fechar menu"
        *ngIf="menuAberto && !menuFixo"
      ></button>

      <aside class="sidebar" id="main-sidebar" [class.open]="menuAberto || menuFixo">
        <div class="sidebar-rail">
          <a class="rail-logo" routerLink="/" aria-label="Ir para o dashboard" title="Dashboard">
            <img src="assets/logo-mark.png" alt="" aria-hidden="true">
          </a>

          <nav class="rail-nav" aria-label="Atalhos principais">
            <div class="rail-nav-group" *ngFor="let grupo of menuGroups" [hidden]="!canAccessGroup(grupo)">
              <button
                class="rail-link rail-group-toggle"
                type="button"
                (click)="toggleGrupoRail(grupo)"
                [class.active]="isGroupActive(grupo) || grupoRailAberto === grupo"
                [attr.aria-expanded]="grupoRailAberto === grupo"
                [attr.aria-label]="grupo.label"
                [title]="grupo.label"
              >
                <svg [ngSwitch]="grupo.items[0].key" viewBox="0 0 24 24" aria-hidden="true">
                  <g *ngSwitchCase="'dashboard'"><path d="M4 19V5M4 19h16M8 16v-5M12 16V8M16 16v-9"></path></g>
                  <g *ngSwitchCase="'notas'"><path d="M7 3h8l4 4v14H7z"></path><path d="M15 3v5h4M10 12h6M10 16h6"></path></g>
                  <g *ngSwitchCase="'clientes'"><circle cx="9" cy="8" r="3"></circle><path d="M3.5 19c1-3 2.8-4.5 5.5-4.5s4.5 1.5 5.5 4.5M16 11h5M18.5 8.5v5"></path></g>
                  <g *ngSwitchCase="'contas_pagar'"><path d="M4 7h16v12H4zM4 10h16M8 15h4M8 4h8"></path></g>
                  <g *ngSwitchCase="'usuarios'"><circle cx="9" cy="8" r="3"></circle><path d="M3.5 19c1-3 2.8-4.5 5.5-4.5s4.5 1.5 5.5 4.5M16 11h5M18.5 8.5v5"></path></g>
                </svg>
              </button>

              <div class="rail-menu-popover" *ngIf="grupoRailAberto === grupo">
                <section class="nav-section rail-menu-section" [class.collapsed]="!grupoRailExpandido">
                  <button
                    class="nav-section-toggle"
                    type="button"
                    (click)="alternarGrupoRailItens()"
                    [attr.aria-expanded]="grupoRailExpandido"
                  >
                    <span class="nav-section-heading"><span class="nav-section-label">{{ grupo.label }}</span></span>
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 10l4 4 4-4"></path></svg>
                  </button>
                  <div class="nav-section-items">
                    <a
                      class="rail-menu-item"
                      *ngFor="let item of itensAcessiveis(grupo)"
                      [routerLink]="item.path"
                      [class.active]="isMenuActive(item)"
                      (click)="selecionarItemMenu()"
                    >
                      <span>{{ item.label }}</span>
                      <svg class="nav-link-chevron" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6"></path></svg>
                    </a>
                  </div>
                </section>
              </div>
            </div>
          </nav>

          <div class="rail-footer">
            <span class="rail-divider" aria-hidden="true"></span>
            <button class="rail-link" type="button" (click)="abrirConfiguracoes()" title="Configurações" aria-label="Configurações">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-1.7 1.7-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5v.2h-2.4v-.2a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1L8 17l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H6v-2.4h.2a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.9L7.3 8.6 9 6.9l.1.1a1.7 1.7 0 0 0 1.9-.3l.1-.1 1.7 1.7-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.5 1h.2V14h-.2a1.7 1.7 0 0 0-1.5 1z"></path></svg>
            </button>
            <button class="rail-link" type="button" (click)="mostrarAjuda()" title="Ajuda" aria-label="Ajuda">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8"></circle><path d="M9.8 9a2.4 2.4 0 1 1 3.7 2c-1 .7-1.5 1.1-1.5 2.2M12 16.5h.01"></path></svg>
            </button>
            <button class="rail-link rail-collapse" type="button" (click)="alternarPainel()" [attr.aria-label]="painelRecolhido ? 'Expandir menu' : 'Recolher menu'" [title]="painelRecolhido ? 'Expandir menu' : 'Recolher menu'">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 5l-7 7 7 7M7 12h13"></path></svg>
            </button>
          </div>
        </div>

        <div class="rail-help-popover" *ngIf="ajudaAberta" role="status">
          <strong>Ajuda rápida</strong>
          <span>Use os grupos do menu para acessar cada área do sistema.</span>
          <button type="button" (click)="mostrarAjuda()">Fechar</button>
        </div>

        <div class="sidebar-panel">
          <header class="sidebar-panel-header">
            <a class="sidebar-panel-logo" routerLink="/" aria-label="Ir para o dashboard" title="Dashboard">
              <img src="assets/logo-mark.png" alt="" aria-hidden="true">
            </a>
            <div class="sidebar-panel-heading">
              <div class="sidebar-panel-title-row">
                <h1>Romaneio Fácil</h1>
                <span class="sidebar-status"><i aria-hidden="true"></i>Operacional</span>
              </div>
              <p>Gestão operacional</p>
            </div>
            <button class="sidebar-collapse-button" type="button" (click)="alternarPainel()" [attr.aria-label]="painelRecolhido ? 'Expandir menu' : 'Recolher menu'" [title]="painelRecolhido ? 'Expandir menu' : 'Recolher menu'">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 5l-7 7 7 7"></path></svg>
            </button>
          </header>

        <div class="brand-block">
          <img class="brand-logo-mark" src="assets/logo-mark.png" alt="" aria-hidden="true">
          <div class="brand-copy">
            <h1>Romaneio Fácil</h1>
            <p>Gestão operacional</p>
          </div>
        </div>

        <button
          class="menu-mode-button"
          type="button"
          (click)="alternarMenuFixo()"
          [attr.aria-label]="menuFixo ? 'Recolher menu' : 'Manter menu aberto'"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path *ngIf="!menuFixo" d="M6 4h12"></path>
            <path *ngIf="!menuFixo" d="M12 4v16"></path>
            <path *ngIf="menuFixo" d="M5 12h14"></path>
            <path *ngIf="menuFixo" d="M9 8l-4 4 4 4"></path>
          </svg>
          <span>{{ menuFixo ? 'Recolher' : 'Fixar' }}</span>
        </button>

        <nav aria-label="Menu principal">
          <section
            class="nav-section"
            *ngFor="let grupo of gruposAcessiveis"
            [class.collapsed]="isGroupCollapsed(grupo)"
          >
            <button
              class="nav-section-toggle"
              type="button"
              (click)="toggleGroup(grupo)"
              [attr.aria-expanded]="!isGroupCollapsed(grupo)"
            >
              <span class="nav-section-heading">
                <span class="nav-section-label">{{ grupo.label }}</span>
              </span>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M8 10l4 4 4-4"></path>
              </svg>
            </button>
            <div class="nav-section-items">
              <a
                *ngFor="let item of itensAcessiveis(grupo)"
                [routerLink]="item.path"
                [class.active]="isMenuActive(item)"
                (click)="selecionarItemMenu()"
              >
                <span class="nav-icon" [attr.data-key]="item.key" aria-hidden="true">
                  <svg [ngSwitch]="item.key" viewBox="0 0 24 24" focusable="false">
                    <g *ngSwitchCase="'dashboard'">
                      <path d="M4 19V5"></path>
                      <path d="M4 19h16"></path>
                      <path d="M8 16v-5"></path>
                      <path d="M12 16V8"></path>
                      <path d="M16 16v-9"></path>
                    </g>
                    <g *ngSwitchCase="'clientes'">
                      <circle cx="12" cy="8" r="3"></circle>
                      <path d="M5 19c1.3-3.1 3.6-4.7 7-4.7s5.7 1.6 7 4.7"></path>
                    </g>
                    <g *ngSwitchCase="'notas'">
                      <path d="M7 3h8l4 4v14H7z"></path>
                      <path d="M15 3v5h4"></path>
                      <path d="M10 12h6"></path>
                      <path d="M10 16h6"></path>
                    </g>
                    <g *ngSwitchCase="'produtos'">
                      <path d="M4 8l8-4 8 4-8 4z"></path>
                      <path d="M4 8v8l8 4 8-4V8"></path>
                      <path d="M12 12v8"></path>
                    </g>
                    <g *ngSwitchCase="'conferencias_entrada'">
                      <path d="M8 4h8"></path>
                      <path d="M9 3h6l1 3H8z"></path>
                      <path d="M6 6h12v15H6z"></path>
                      <path d="M9 14l2 2 4-5"></path>
                    </g>
                    <g *ngSwitchCase="'contagens_nova'">
                      <path d="M5 5h14v14H5z"></path>
                      <path d="M12 8v8"></path>
                      <path d="M8 12h8"></path>
                    </g>
                    <g *ngSwitchCase="'contagens'">
                      <path d="M4 12a8 8 0 1 0 2.3-5.7"></path>
                      <path d="M4 5v5h5"></path>
                      <path d="M12 8v5l3 2"></path>
                    </g>
                    <g *ngSwitchCase="'pagamentos'">
                      <path d="M4 7h16v11H4z"></path>
                      <path d="M4 10h16"></path>
                      <path d="M8 15h4"></path>
                    </g>
                    <g *ngSwitchCase="'contas_pagar'">
                      <path d="M5 4h14v16H5z"></path>
                      <path d="M8 8h8M8 12h8M8 16h5"></path>
                    </g>
                    <g *ngSwitchCase="'contas_receber'">
                      <path d="M4 7h16v12H4z"></path>
                      <path d="M4 10h16M8 15h4"></path>
                      <path d="M8 4h8"></path>
                    </g>
                    <g *ngSwitchCase="'caixa'">
                      <path d="M4 8h16v12H4z"></path>
                      <path d="M7 8V5h10v3M8 13h8M12 11v4"></path>
                    </g>
                    <g *ngSwitchCase="'fornecedores'">
                      <circle cx="9" cy="8" r="3"></circle>
                      <path d="M3.5 19c1-3 2.8-4.5 5.5-4.5s4.5 1.5 5.5 4.5"></path>
                      <path d="M16 11h5M18.5 8.5v5"></path>
                    </g>
                    <g *ngSwitchCase="'ocorrencias'">
                      <path d="M12 4l8 14H4z"></path>
                      <path d="M12 9v4"></path>
                      <path d="M12 16h.01"></path>
                    </g>
                    <g *ngSwitchCase="'usuarios'">
                      <circle cx="9" cy="8" r="3"></circle>
                      <path d="M3.5 19c1-3 2.8-4.5 5.5-4.5s4.5 1.5 5.5 4.5"></path>
                      <path d="M15 11a2.7 2.7 0 1 0 0-5.4"></path>
                      <path d="M16 14.5c2.2.3 3.7 1.8 4.5 4.5"></path>
                    </g>
                    <g *ngSwitchDefault>
                      <circle cx="12" cy="12" r="7"></circle>
                    </g>
                  </svg>
                </span>
                <span class="nav-link-label">{{ item.label }}</span>
                <svg class="nav-link-chevron" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6"></path></svg>
              </a>
            </div>
          </section>
        </nav>

        <div class="sidebar-panel-tools">
          <button type="button" (click)="abrirConfiguracoes()" aria-label="Configurações">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-1.7 1.7-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5v.2h-2.4v-.2a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1L8 17l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H6v-2.4h.2a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.9L7.3 8.6 9 6.9l.1.1a1.7 1.7 0 0 0 1.9.3l.1-.1 1.7 1.7-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.5 1h.2V14h-.2a1.7 1.7 0 0 0-1.5 1z"></path></svg>
            <span>Configurações</span>
          </button>
          <button type="button" (click)="mostrarAjuda()" aria-label="Ajuda">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8"></circle><path d="M9.8 9a2.4 2.4 0 1 1 3.7 2c-1 .7-1.5 1.1-1.5 2.2M12 16.5h.01"></path></svg>
            <span>Ajuda</span>
          </button>
        </div>

        <div class="sidebar-footer">
          <button class="user-card" type="button" (click)="alternarMenuUsuario()" [attr.aria-expanded]="menuUsuarioAberto" aria-haspopup="menu">
            <span class="user-avatar" aria-hidden="true">{{ userInitials }}</span>
            <div class="user-card-copy">
              <small>USUÁRIO</small>
              <strong>{{ auth.usuario?.nome }}</strong>
            </div>
            <svg class="user-card-chevron" [class.open]="menuUsuarioAberto" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 10l5 5 5-5"></path>
            </svg>
          </button>
          <div class="user-menu" *ngIf="menuUsuarioAberto" role="menu">
            <button class="user-menu-item" type="button" role="menuitem" (click)="abrirConfiguracoes()">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-1.7 1.7-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5v.2h-2.4v-.2a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1L8 17l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H6v-2.4h.2a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.9L7.3 8.6 9 6.9l.1.1a1.7 1.7 0 0 0 1.9.3 1.7 1.7 0 0 0 1-1.5v-.2h2.4v.2a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1 1.7 1.7-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.5 1h.2V14h-.2a1.7 1.7 0 0 0-1.5 1z"></path></svg>
              <span>Configurações</span>
            </button>
            <button class="user-menu-item" type="button" role="menuitem" (click)="sair()">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 5H5v14h5"></path><path d="M13 8l4 4-4 4"></path><path d="M8 12h9"></path></svg>
              <span>Sair</span>
            </button>
          </div>
        </div>
        </div>
      </aside>

      <div class="modal-backdrop profile-modal-backdrop" *ngIf="perfilAberto" (click)="fecharPerfil($event)">
        <section class="modal profile-modal" role="dialog" aria-modal="true" aria-labelledby="titulo-meu-perfil">
          <header class="modal-header">
            <div>
              <h3 id="titulo-meu-perfil">Meu perfil</h3>
              <p>Atualize seus dados de acesso</p>
            </div>
            <button class="ghost modal-close" type="button" (click)="fecharPerfil()" aria-label="Fechar">X</button>
          </header>

          <form class="profile-form" [formGroup]="perfilForm" (ngSubmit)="salvarPerfil()">
            <label>Nome<input formControlName="nome" autocomplete="name"></label>
            <label>E-mail<input [value]="auth.usuario?.email || ''" type="email" readonly></label>
            <label>CPF<input formControlName="cpf" inputmode="numeric" autocomplete="off" placeholder="Informe para cadastrar ou substituir"></label>
            <p class="field-hint" *ngIf="perfilCpfCadastrado">Já existe um CPF cadastrado. Informe outro somente se precisar substituí-lo.</p>
            <p class="error" *ngIf="erroPerfil">{{ erroPerfil }}</p>
            <p class="success" *ngIf="sucessoPerfil">{{ sucessoPerfil }}</p>
            <div class="profile-actions">
              <button type="submit" [disabled]="perfilForm.invalid || salvandoPerfil">{{ salvandoPerfil ? 'Salvando...' : 'Salvar dados' }}</button>
            </div>
          </form>

          <div class="profile-divider"></div>

          <form class="profile-form" [formGroup]="senhaForm" (ngSubmit)="alterarSenha()">
            <div class="profile-section-title">
              <h4>Alterar senha</h4>
              <p>Use uma senha com pelo menos 8 caracteres.</p>
            </div>
            <label>Senha atual<input type="password" formControlName="senha_atual" autocomplete="current-password"></label>
            <label>Nova senha<input type="password" formControlName="nova_senha" autocomplete="new-password"></label>
            <label>Confirmar nova senha<input type="password" formControlName="confirmar_senha" autocomplete="new-password"></label>
            <p class="error" *ngIf="erroSenha">{{ erroSenha }}</p>
            <p class="success" *ngIf="sucessoSenha">{{ sucessoSenha }}</p>
            <div class="profile-actions">
              <button type="submit" [disabled]="senhaForm.invalid || alterandoSenha">{{ alterandoSenha ? 'Alterando...' : 'Alterar senha' }}</button>
            </div>
          </form>
        </section>
      </div>

      <main class="content">
        <div class="content-frame">
          <router-outlet></router-outlet>
        </div>
      </main>
    </div>
  `
})
export class AppComponent {
  auth = inject(AuthService);
  private api = inject(ApiService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  menuGroups = MENU_GROUPS;
  menuAberto = false;
  menuFixo = false;
  painelRecolhido = false;
  grupoRailAberto: MenuGroup | null = null;
  grupoRailExpandido = true;
  menuUsuarioAberto = false;
  ajudaAberta = false;
  gruposRecolhidos: Record<string, boolean> = {};
  perfilAberto = false;
  perfilCpfCadastrado = false;
  salvandoPerfil = false;
  alterandoSenha = false;
  erroPerfil = '';
  sucessoPerfil = '';
  erroSenha = '';
  sucessoSenha = '';

  perfilForm = this.fb.nonNullable.group({
    nome: ['', Validators.required],
    cpf: ['']
  });

  senhaForm = this.fb.nonNullable.group({
    senha_atual: ['', Validators.required],
    nova_senha: ['', [Validators.required, Validators.minLength(8)]],
    confirmar_senha: ['', Validators.required]
  });

  constructor() {
    this.menuFixo = this.getMenuFixoSalvo();
    this.painelRecolhido = this.getPainelRecolhidoSalvo();
    this.gruposRecolhidos = this.getGruposRecolhidosSalvos();
  }

  get userInitials() {
    const nome = this.auth.usuario?.nome?.trim() || 'Usuário';
    return nome
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }

  isMenuActive(item: MenuPermission) {
    const url = this.router.url.split('?')[0].split('#')[0];

    if (item.key === 'contagens_nova') {
      return url === '/contagens/nova';
    }

    if (item.key === 'contagens') {
      return url.startsWith('/contagens') && url !== '/contagens/nova';
    }

    return item.exact ? url === item.path : url === item.path || url.startsWith(`${item.path}/`);
  }

  canAccessGroup(grupo: MenuGroup) {
    return grupo.items.some((item) => this.auth.canAccess(item.key));
  }

  get gruposAcessiveis() {
    return this.menuGroups.filter((grupo) => this.canAccessGroup(grupo));
  }

  itensAcessiveis(grupo: MenuGroup) {
    return grupo.items.filter((item) => this.auth.canAccess(item.key));
  }

  isGroupActive(grupo: MenuGroup) {
    return this.itensAcessiveis(grupo).some((item) => this.isMenuActive(item));
  }

  isGroupCollapsed(grupo: MenuGroup) {
    return !!this.gruposRecolhidos[this.getGroupKey(grupo)];
  }

  toggleGroup(grupo: MenuGroup) {
    const key = this.getGroupKey(grupo);
    this.gruposRecolhidos = {
      ...this.gruposRecolhidos,
      [key]: !this.gruposRecolhidos[key]
    };

    this.salvarGruposRecolhidos();
  }

  toggleGrupoRail(grupo: MenuGroup) {
    if (this.grupoRailAberto === grupo) {
      this.grupoRailAberto = null;
      return;
    }

    this.grupoRailAberto = grupo;
    this.grupoRailExpandido = true;
  }

  alternarGrupoRailItens() {
    this.grupoRailExpandido = !this.grupoRailExpandido;
  }

  fecharGrupoRail() {
    this.grupoRailAberto = null;
  }

  selecionarItemMenu() {
    this.fecharGrupoRail();

    if (typeof window === 'undefined' || !window.matchMedia('(max-width: 900px)').matches) {
      return;
    }

    this.fecharMenu();
    if (this.menuFixo) {
      this.definirMenuFixo(false);
    }
  }

  sair() {
    this.fecharMenu();
    this.menuUsuarioAberto = false;
    this.auth.logout();
    this.router.navigateByUrl('/login');
  }

  alternarMenuUsuario() {
    this.menuUsuarioAberto = !this.menuUsuarioAberto;
  }

  alternarPainel() {
    this.painelRecolhido = !this.painelRecolhido;
    this.fecharGrupoRail();

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('romaneio-painel-recolhido', this.painelRecolhido ? '1' : '0');
    }
  }

  mostrarAjuda() {
    this.ajudaAberta = !this.ajudaAberta;
  }

  abrirConfiguracoes() {
    this.menuUsuarioAberto = false;
    this.abrirPerfil();
  }

  abrirPerfil() {
    const usuario = this.auth.usuario;
    this.perfilAberto = true;
    this.erroPerfil = '';
    this.sucessoPerfil = '';
    this.erroSenha = '';
    this.sucessoSenha = '';
    this.perfilForm.reset({ nome: usuario?.nome || '', cpf: '' });
    this.senhaForm.reset({ senha_atual: '', nova_senha: '', confirmar_senha: '' });

    this.api.meuPerfil().subscribe({
      next: (perfil) => {
        this.perfilCpfCadastrado = !!perfil.cpf_cadastrado;
        this.perfilForm.patchValue({ nome: perfil.nome || '' });
        this.auth.atualizarUsuario(perfil);
      },
      error: (error) => (this.erroPerfil = error.error?.message || 'Não foi possível carregar o perfil')
    });
  }

  salvarPerfil() {
    if (this.perfilForm.invalid || this.salvandoPerfil) return;
    this.salvandoPerfil = true;
    this.erroPerfil = '';
    this.sucessoPerfil = '';
    const raw = this.perfilForm.getRawValue();
    const cpf = raw.cpf.replace(/\D/g, '');

    this.api.atualizarMeuPerfil({ nome: raw.nome.trim(), cpf: cpf || undefined }).subscribe({
      next: (perfil) => {
        this.auth.atualizarUsuario(perfil);
        this.perfilCpfCadastrado = this.perfilCpfCadastrado || !!cpf;
        this.sucessoPerfil = 'Dados atualizados com sucesso.';
        this.salvandoPerfil = false;
      },
      error: (error) => {
        this.erroPerfil = error.error?.message || 'Não foi possível atualizar os dados';
        this.salvandoPerfil = false;
      }
    });
  }

  alterarSenha() {
    if (this.senhaForm.invalid || this.alterandoSenha) return;
    const raw = this.senhaForm.getRawValue();
    if (raw.nova_senha !== raw.confirmar_senha) {
      this.erroSenha = 'A confirmação da nova senha não confere.';
      this.sucessoSenha = '';
      return;
    }

    this.alterandoSenha = true;
    this.erroSenha = '';
    this.sucessoSenha = '';
    this.api.alterarMinhaSenha({ senha_atual: raw.senha_atual, nova_senha: raw.nova_senha }).subscribe({
      next: () => {
        this.senhaForm.reset({ senha_atual: '', nova_senha: '', confirmar_senha: '' });
        this.sucessoSenha = 'Senha alterada com sucesso.';
        this.alterandoSenha = false;
      },
      error: (error) => {
        this.erroSenha = error.error?.message || 'Não foi possível alterar a senha';
        this.alterandoSenha = false;
      }
    });
  }

  fecharPerfil(event?: Event) {
    if (event && event.target !== event.currentTarget) return;
    this.perfilAberto = false;
  }

  toggleMenu() {
    this.fecharGrupoRail();

    if (this.menuFixo) {
      this.definirMenuFixo(false);
      return;
    }

    this.menuAberto = !this.menuAberto;
  }

  fecharMenu() {
    this.menuAberto = false;
    this.menuUsuarioAberto = false;
    this.fecharGrupoRail();
  }

  alternarMenuFixo() {
    this.definirMenuFixo(!this.menuFixo);
  }

  private definirMenuFixo(fixo: boolean) {
    this.menuFixo = fixo;
    this.menuAberto = false;

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('romaneio-menu-fixo', fixo ? '1' : '0');
    }
  }

  private getMenuFixoSalvo() {
    if (typeof localStorage === 'undefined') {
      return false;
    }

    return localStorage.getItem('romaneio-menu-fixo') === '1';
  }

  private getPainelRecolhidoSalvo() {
    if (typeof localStorage === 'undefined') {
      return false;
    }

    return localStorage.getItem('romaneio-painel-recolhido') === '1';
  }

  private getGroupKey(grupo: MenuGroup) {
    return grupo.label;
  }

  private salvarGruposRecolhidos() {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('romaneio-grupos-menu-recolhidos', JSON.stringify(this.gruposRecolhidos));
    }
  }

  private getGruposRecolhidosSalvos() {
    if (typeof localStorage === 'undefined') {
      return {};
    }

    try {
      return JSON.parse(localStorage.getItem('romaneio-grupos-menu-recolhidos') || '{}');
    } catch {
      return {};
    }
  }
}
